<template>
	<b-container fluid class="px-0 h-100 overflow-hidden">
		<b-row class="mx-0 h-100 d-flex flex-row align-content-stretch">
			<b-col v-show="project" cols="*" class="px-0 mx-0">
				<visualization-tools :scenario="selectedScenario" />
			</b-col>
			<b-col v-show="projectListIsOpen" cols="*" class="h-100">
				<project-list />
			</b-col>
			<b-col cols="*" class="px-0 mx-0">
				<portal-target
					name="smartpanel-slim-left-1"
					multiple
					class="panel"
					:class="{ slim: showSlimLeft1 }"
				/>
				<portal-target
					name="smartpanel-slim-left-2"
					multiple
					class="panel slim"
				/>
			</b-col>
			<b-col class="px-0 mx-0">
				<create-tour-stop
					v-if="createTourStopIsOpen"
					class="create-tour-stop"
					panel="middle"
					:scenario="selectedScenario"
					:expanded="false"
					@remove="setCreateTourStopIsOpen(false)"
				/>
				<cesium-viewer
					ref="viewer"
					@entity-clicked="openElementEditor"
				/>
				<portal-target
					name="smartpanel-slim"
					multiple
					class="panel slim"
				/>
				<portal-target
					name="smartpanel-wide"
					multiple
					class="panel wide"
				/>
			</b-col>
			<b-col v-show="project" cols="*" class="px-0 mx-0">
				<project-tools
					:scenario="selectedScenario"
					@select-entity.capture="openElementEditor"
				/>
			</b-col>
		</b-row>
		<b-modal
			id="createProject"
			:title="$t('create-new-project.title')"
			hide-backdrop
			:ok-title="$t('create-new-project.action-startboundary')"
			@ok="captureBoundary"
		>
			<b-card class="mt-5 mx-auto bg-none border-none">
				<b-row>
					<b-col>
						<b-form-group
							:label="$t('create-new-project.label-location')"
						>
							<geocoder @locationSelected="setLocation" />
						</b-form-group>
						<b-form-group
							:label="$t('create-new-project.label-name')"
						>
							<b-form-input v-model="newProject.name" />
						</b-form-group>
						<b-form-group
							:label="
								$t('appbar.settings-menu.ManageProjects.units')
							"
							label-for="units"
						>
							<b-form-select
								id="units"
								v-model="newProject.units"
								:options="unitOptions"
							/>
						</b-form-group>
					</b-col>
				</b-row>
			</b-card>
		</b-modal>
		<div v-if="activeTour">
			<TourPlayback />
		</div>
	</b-container>
</template>

<script>
import { mapMutations, mapState, mapGetters } from 'vuex';

import { DateTime } from 'luxon';
import TourPlayback from '@/components/Project/Tool/TourPlayback.vue';
import { polygon } from '@turf/helpers';
import bbox from '@turf/bbox'
import CreateTourStop from '../../components/Project/Tool/CreateTourStop.vue';
import ProjectTools from '../../components/Project/ProjectTools.vue';
import VisualizationTools from '../../components/Project/VisualizationTools.vue';
import ProjectList from '../../components/Project/ProjectList/ProjectList.vue';
import CesiumViewer, { AUTHORING_SHAPE } from '../../components/Project/Viewport/CesiumViewer.vue';
import AssetEditor from '../../components/Project/Tool/AssetEditor/AssetEditor.vue';
import 'cesium/Build/Cesium/Widgets/widgets.css';
import Geocoder from '../../components/Geocoder.vue';


	export default {
		name: 'ProjectDetail',
		components: {
			ProjectTools,
			CesiumViewer,
			ProjectList,
			VisualizationTools,
			CreateTourStop,
			TourPlayback,
			Geocoder,
		},
		props: {
			scenarioId: {
				type: String,
				default: null,
			},
		},
		data() {
			return {
				selectedScenario: {},
				newProject: { name: null, location: null },
				selectedLocation: {},
				unitOptions: [
					{
						text: this.$t(
							'appbar.settings-menu.ManageProjects.imperial'
						),
						value: 'IMPERIAL',
					},
					{
						text: this.$t(
							'appbar.settings-menu.ManageProjects.metric'
						),
						value: 'METRIC',
					},
				],
			};
		},
		computed: {
			...mapGetters(['activeTour']),
			...mapState([
				'createTourStopIsOpen',
				'openPanels',
				'projectListIsOpen',
				'isLoading',
				'project',
			]),
			showSlimLeft1() {
				return (
					this.openPanels.visualizationTools.slim1.tools.length > 0
				);
			},
		},
		watch: {
			scenarioId: {
				handler(v) {
					if (v != null) {
						this.loadScenario(v);
					}
				},
				immediate: true,
			},
		},
		mounted() {
			this.$store.state.bg_class = 'bg-water';
			this.newProject.enterpriseId = this.$store.state.user.currentEnterprise.id;
		},
		methods: {
			...mapMutations([
				'openPanel',
				'setCreateTourStopIsOpen',
				'setProjectListIsOpen',
				'setTours',
				'setLoading',
				'setElementEditorEntityDataId',
				'closeAllPanels',
			]),
			async loadScenario(id) {
				this.closeAllPanels();
				this.setProjectListIsOpen(false);
				this.$czm('zoomBack');
				const getScenario = async () => {
					this.setLoading(true);

					const scenarioResponse = await this.axios.get(
						`/scenarios/${id}`
					);
					this.$store.commit('setScenario', id);
					this.selectedScenario = scenarioResponse.data;
					this.$store.commit(
						'setProject',
						scenarioResponse.data.project
					);
					// clear the displayed scenario before the chosen one is loaded
					await this.$czm('removeScenePrimitives');
					if (scenarioResponse.data.entities.length > 0) {
						await this.$czm('loadScenarioData', this.selectedScenario);
						this.$czm('addBuildings');
					} else {
						const projectBounds = this.selectedScenario.project
							.boundary.coordinates;
						const newbbox = bbox(polygon(projectBounds));
						await this.$czm(
							'constructRectangleFromDegrees',
							newbbox,
							this.zoomToLocation
						);
					}

					this.axios
						.get(`/scenarios/${id}/tours`)
						.then(({ data }) => {
							for (const tour of data) {
								if (tour.data.stops)
									for (const stop of tour.data.stops) {
										stop.modified = DateTime.fromISO(
											stop.modified
										);
									}
							}
							this.setTours(data);
						});

					/**
					 * Bind workflows onto tool buttons, this is just for demo purposes
					 * @todo
					 */
					/* eslint-disable */
					const demoButtons = Array.prototype.slice.call(
						document.querySelectorAll('[data-tool]'),
						-4
					);
					demoButtons[0].addEventListener('click', async () => {
						try {
							this.$czm('toggleBrightnessActive');
						} catch (e) {
							/** */
						}
					});
					demoButtons[1].addEventListener('click', async () => {
						try {
							const place = await this.$czm(
								'enterPlacementWorkflow'
							);
							this.setMarker(place[0]);
						} catch (e) {
							/** */
						}
					});
					demoButtons[2].addEventListener('click', async () => {
						try {
							await this.$czm(
								'enterShapeAuthoringWorkflow',
								AUTHORING_SHAPE.PATH,
								console.log
							);
						} catch (e) {
							/** */
						}
					});
					demoButtons[3].addEventListener('click', async () => {
						try {
							await this.$czm(
								'enterTourCreationWorkflow',
								this.doConsole
							);
						} catch (e) {
							console.log(e.message);
						}
					});
					/* eslint-enable */
				};

				if (!this.selectedScenario.id) {
					getScenario();
				} else if (this.selectedScenario.id !== id) {
					const confirmation = await this.$msgBoxConfirm(
						this.$t('projectlist.load-scenario.modalOptions')
					);
					if (confirmation) {
						getScenario();
					}
				}
			},
			setMarker(pos) {
				console.log(pos);
				this.$czm('setMarkerInPos2', pos);
			},
			doConsole(input) {
				// eslint-disable-next-line no-console
				console.log(input);
			},
			openElementEditor(entity) {
				const isOpen = this.openPanels.projectTools.wide.tools.find(
					(tool) => tool.componentName === 'AssetEditor'
				);
				if (entity && !isOpen) {
					this.openPanel({
						area: 'projectTools',
						panel: {
							componentName: AssetEditor.name,
							searchable: false,
							expandable: false,
							wide: true,
						},
						smartPanel: 'wide',
						wide: true,
					});
				}
				this.setElementEditorEntityDataId(entity.id);
				this.$root.$emit('entity-clicked', entity);
			},
			/**
			 * callback function upon selection from Geocoder
			 * constructs Cesium rectangle from the bounding box
			 * returned by the Geocoder.
			 */
			async setLocation(loc) {
				this.selectedLocation = loc;
				this.newProject.location = loc.display_name;
				const projectBbox = [
					// reorder the bounding box coordinates
					this.selectedLocation.boundingbox[2],
					this.selectedLocation.boundingbox[0],
					this.selectedLocation.boundingbox[3],
					this.selectedLocation.boundingbox[1],
				];
				await this.$czm(
					'constructRectangleFromDegrees',
					projectBbox,
					this.zoomToLocation
				);
				// check return value
			},
			/**
			 * callback function for zooming to selected location
			 */
			zoomToLocation(loc) {
				this.$czm('focus', loc);
			},
			/**
			 * start the shape authoring workflow
			 */
			async captureBoundary() {
				try {
					const boundary = await this.$czm(
						'enterShapeAuthoringWorkflow',
						AUTHORING_SHAPE.POLYGON
					);
					await this.createProject(boundary);
				} catch (e) {
					// eslint-disable-next-line no-console
					console.log(e); /** */
				}
			},
			/**
			 * after confirmation in shape authoring workflow, assemble the request to backend
			 * toast response
			 */
			checkPolygon(data) {
				if (!data || !Array.isArray(data)) return false;
				const boundaryGeoJson = {
					type: 'Polygon',
					coordinates: [data],
				};
				this.newProject.boundary = boundaryGeoJson;
				return boundaryGeoJson;
			},
			async createProject(data) {
				await this.$czm(
					'constructGeoPolyFromCartesian',
					data,
					this.checkPolygon
				);
				const newProject = await this.axios
					.post('/projects', this.newProject)
					.then((response) =>
						response.status === 201 ? response.data : null
					)
					.catch(() => null);
				if (newProject) {
					this.$bvToast.toast(
						this.$t('create-new-project.response-successful'),
						{
							autoHideDelay: 5000,
							appendToast: false,
							variant: 'success',
						}
					);
					this.$store.commit('setProject', newProject);
					this.$store.dispatch('fetchProjectList');
				} else {
					this.$bvToast.toast(
						this.$t('create-new-project.response-failed'),
						{
							autoHideDelay: 5000,
							appendToast: false,
							variant: 'warning',
						}
					);
				}
			},
		},
	};
</script>

<style scoped>
	.reset-boundary-btn {
		position: absolute;
		left: 50%;
		top: 50%;
		transform: translate(-50%);
		border-radius: 100%;
		width: 3rem;
		height: 3rem;
		box-shadow: 0 0 1em #000;
	}
	.panel {
		max-height: calc(100vh - 45px);
		position: relative;
		float: right;
		overflow-y: auto;
		scrollbar-width: none; /*  */
	}
	.panel.slim:not(:empty) {
		width: 20.5rem;
	}
	.panel.wide:not(:empty) {
		width: 41rem;
	}
	.create-tour-stop {
		position: fixed;
		bottom: 0;
		left: 50vw;
		transform: translateX(-50%);
		width: 658.6px !important;
		z-index: 1;
	}
</style>
